import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { Checkbox, Input } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { getSortedList, useDebounce } from '@skytech/utils'
import { getQueryFilter } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import CloseIcon from 'mdi-react/CloseIcon'
import FilterOutlineIcon from 'mdi-react/FilterOutlineIcon'
import FormatListChecksIcon from 'mdi-react/FormatListChecksIcon'
import { useNavigate } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useIsAdmin } from '@/hooks/access'
import { useSearchParamsByKey, useSortParams } from '@/hooks/searchParams'
import adminResources from '@/modules/admin/resources'
import { commonEcomPolicyResources } from '@/resources/ecom-policy'
import { commonQueries } from '@/resources/queries'
import { getEcomPolicyIdList } from '@/util/getEcomPolicyIdList'

import queries from '../queries'
import type { CostObject } from '../resources'

import AssignPoliciesPopup from './AssignPoliciesPopup'
import { CostObjectList } from './CostObjectList'
import PolicyFilter from './PolicyFilter'
import type { PolicyId } from './types'

import './index.styles.scss'

const CostObjectListPage: FC = () => {
  const navigate = useNavigate()
  const translate = useTranslation()
  const { locale } = useAppContext()

  const [isPolicyFilterPopupOpen, setIsPolicyFilterPopupOpen] = useState(false)
  const [isAssignPolicyPopupOpen, setIsAssignPolicyPopupOpen] = useState(false)
  const [selectedEcomPolicyFilterIds, setSelectedEcomPolicyFilterIds] = useState<PolicyId[] | null>(
    null
  )
  const [selectedTelePolicyFilterIds, setSelectedTelePolicyFilterIds] = useState<PolicyId[] | null>(
    null
  )

  const [querySearch, setQuerySearch] = useSearchParamsByKey('search')
  const [searchFilter, setSearchFilter] = useState(querySearch || '')
  const [{ querySort, querySortBy }] = useSortParams()
  const isAdmin = useIsAdmin()

  const [queryShowHidden, setQueryShowHidden] = useSearchParamsByKey('show-hidden')
  const [showHiddenFilter, setShowHiddenFilter] = useState(Boolean(queryShowHidden))

  const debouncedSearchTerm = useDebounce(searchFilter, 500)

  const { data: availableEcomPolicies } = useQuery(
    commonQueries.policyList(),
    commonEcomPolicyResources.getList
  )
  const { data: telePolicies } = useQuery(
    queries.adminTelePoliciesList(),
    adminResources.getTelePolicyList,
    {
      enabled: isAdmin,
    }
  )

  const ecomPolicies = availableEcomPolicies?.filter(policy => policy.isActive) || []

  useEffect(() => {
    setSearchFilter(querySearch || '')
    setShowHiddenFilter(queryShowHidden ? JSON.parse(queryShowHidden) : false)
  }, [querySearch, queryShowHidden])

  useEffect(() => {
    setQuerySearch(debouncedSearchTerm)
  }, [debouncedSearchTerm, setQuerySearch])

  const { data: costObjectList } = useQuery(
    queries.adminCostObjectList(),
    adminResources.getCostObjectList
  )

  const stringMatcher = (item: CostObject) => [
    item.name || '',
    translate(`cost_object_benefit_type/${item.benefitType}`),
    item.employeeNo || '',
    item.code,
    item.departmentName,
    item.departmentCode,
    item.customerName,
    item.email || '',
    item.accountingCode || '',
    item.dim1 || '',
    item.dim2 || '',
    item.dim3 || '',
    item.dim4 || '',
    item.workLocation || '',
  ]

  const costObjectsFiltered =
    costObjectList?.filter(costObject => {
      const isActive = !costObject.isHidden

      const isVisible = showHiddenFilter || (!showHiddenFilter && isActive)
      const ecomPolicyFilter =
        selectedEcomPolicyFilterIds && selectedEcomPolicyFilterIds?.length > 0
          ? selectedEcomPolicyFilterIds
          : undefined
      const isEcomPolicyFilterMet = ecomPolicyFilter
        ? !ecomPolicyFilter.find(
            policyId => !getEcomPolicyIdList(costObject.policyIds || '').includes(policyId)
          )
        : true

      const isTelePolicyFilterMet = getIsTelePolicyFilterMet({
        costObject,
        selectedTelePolicyFilterIds,
      })
      const isQueryFilterMet = getQueryFilter(searchFilter, stringMatcher)(costObject)

      return isQueryFilterMet && isVisible && isEcomPolicyFilterMet && isTelePolicyFilterMet
    }) || []

  const sortedList = getSortedList({ list: costObjectsFiltered, querySort, querySortBy, locale })
  const isRemovePolicyFilterButtonVisible =
    (selectedEcomPolicyFilterIds && selectedEcomPolicyFilterIds.length > 0) ||
    (selectedTelePolicyFilterIds && selectedTelePolicyFilterIds.length > 0)
  const isAssignPoliciesButtonVisible =
    isAdmin &&
    (availableEcomPolicies?.length !== 0 || telePolicies?.length !== 0) &&
    costObjectsFiltered?.length > 0
  const isFiltersPopupAvailable = ecomPolicies || telePolicies

  return (
    <Container onSwipedLeft={() => navigate('/')} className="cost-objects">
      <Container.Header>
        <Translation id="Employees" />
      </Container.Header>

      <div className="table-actions">
        <Input
          id="cost-object-search"
          value={searchFilter}
          placeholder={translate('Search')}
          onChange={e => setSearchFilter(e.target.value)}
        />

        <div className="filters">
          {isFiltersPopupAvailable && (
            <div
              className={`policy-filter ${
                isRemovePolicyFilterButtonVisible ? 'remove-policy-visible' : ''
              }`}
            >
              <Button
                className={twJoin(
                  isRemovePolicyFilterButtonVisible && 'mr-0 rounded-br-none rounded-tr-none',
                  !isRemovePolicyFilterButtonVisible && 'mr-3'
                )}
                Icon={<FilterOutlineIcon />}
                onClick={() => setIsPolicyFilterPopupOpen(true)}
              >
                <Translation id="Filter by policy" />
              </Button>

              {isRemovePolicyFilterButtonVisible && (
                <Button
                  className="ml-0 mr-3 rounded-bl-none rounded-tl-none border-l-2 border-gray-300 pl-1.5 pr-1"
                  Icon={<CloseIcon />}
                  onClick={() => {
                    setSelectedEcomPolicyFilterIds(null)
                    setSelectedTelePolicyFilterIds(null)
                  }}
                />
              )}

              {isPolicyFilterPopupOpen && (
                <PolicyFilter
                  currentEcomPolicyFilter={selectedEcomPolicyFilterIds}
                  currentTelePolicyFilter={selectedTelePolicyFilterIds}
                  onClose={() => setIsPolicyFilterPopupOpen(false)}
                  onSetEcomPolicyFilter={setSelectedEcomPolicyFilterIds}
                  onSetTelePolicyFilter={setSelectedTelePolicyFilterIds}
                />
              )}
            </div>
          )}

          {isAssignPolicyPopupOpen && (
            <AssignPoliciesPopup
              costObjects={sortedList}
              onClose={() => setIsAssignPolicyPopupOpen(false)}
            />
          )}

          <Checkbox
            id="show-hidden-input"
            className="show-hidden-input"
            marginTop={0}
            marginLeft={0}
            checked={queryShowHidden ? JSON.parse(queryShowHidden) : false}
            onChange={() => {
              const updatedShowHidden = queryShowHidden
                ? JSON.parse(queryShowHidden)
                  ? 'false'
                  : 'true'
                : 'true'
              setQueryShowHidden(updatedShowHidden)
            }}
            label={<Translation id="Show hidden" />}
          />
        </div>

        {isAssignPoliciesButtonVisible && (
          <Button Icon={<FormatListChecksIcon />} onClick={() => setIsAssignPolicyPopupOpen(true)}>
            <Translation id="Assign policies" />
          </Button>
        )}
      </div>

      <CostObjectList costObjects={sortedList} searchFilter={searchFilter} />
    </Container>
  )
}

export default CostObjectListPage

function getIsTelePolicyFilterMet({
  selectedTelePolicyFilterIds,
  costObject,
}: {
  selectedTelePolicyFilterIds: PolicyId[] | null
  costObject: CostObject
}) {
  const telePolicyFilter =
    selectedTelePolicyFilterIds && selectedTelePolicyFilterIds?.length > 0
      ? selectedTelePolicyFilterIds
      : undefined

  let isTelePolicyFilterMet = true
  if (!telePolicyFilter) return isTelePolicyFilterMet
  if (telePolicyFilter?.[0] === 'no-policy') {
    isTelePolicyFilterMet = !costObject.telePolicyId
    return isTelePolicyFilterMet
  }
  if (telePolicyFilter[0] !== costObject.telePolicyId) isTelePolicyFilterMet = false

  return isTelePolicyFilterMet
}
